import {StyleSheet, css} from 'aphrodite';
import classNames from 'classnames';
import React, {useState, useCallback} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import DataCaptureAssistantChat from 'llm/data-capture/DataCaptureAssistantChat';
import {DeepResearchChat} from 'llm/deep-research/DeepResearchChat';
import {CrumbLink, SplitBreadcrumbs} from 'nav/Breadcrumbs';
import ListingHeader from 'nav/sidepanel/header/ListingHeader';
import ToggleDropdown from 'nav/ToggleDropdown';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {PURPLE_DARK} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';

enum ChatType {
  DEEP_RESEARCH = 'DEEP_RESEARCH',
  DATA_CAPTURE = 'DATA_CAPTURE',
}

interface AssistantSelectorProps {
  chatType: ChatType;
  onUpdateChatType: (chatType: ChatType) => void;
}

/**
 * Temporary picker UI to allow testing the data capture assistant.
 */
function AssistantSelector({chatType, onUpdateChatType}: AssistantSelectorProps): JSX.Element {
  const assistantOptions = [
    {value: ChatType.DEEP_RESEARCH, label: 'Deep Research'},
    {value: ChatType.DATA_CAPTURE, label: 'Data Capture Assistant'},
  ];

  return (
    <Dropdown
      className={css(styles.assistantDropdown)}
      menu={
        <Menu width={MenuWidth.MEDIUM}>
          {assistantOptions
            .filter((option) => option.value !== chatType)
            .map((option) => (
              <Option
                key={option.value}
                onSelect={() => {
                  onUpdateChatType(option.value);
                }}
              >
                {option.label}
              </Option>
            ))}
        </Menu>
      }
    >
      <Button size={ButtonSize.MINI} buttonStyle={ButtonStyle.SECONDARY}>
        More assistants
      </Button>
    </Dropdown>
  );
}

export interface DeepResearchSidepanelProps {
  isExpanded: boolean;
}

const chatIdHooks = makeUseGlobalClientState<string | null>(null);

export function DeepResearchSidepanel(props: DeepResearchSidepanelProps): JSX.Element {
  // Use a temporary enum and UI to allow switching to the data capture agent.
  const [chatType, setChatType] = useState<ChatType>(ChatType.DEEP_RESEARCH);
  const [chatId, setChatId] = chatIdHooks.useGlobalClientState('deep-research-sidepanel');
  const onCreateChat = useCallback(
    (chatId: string) => {
      setChatId(chatId);
    },
    [setChatId]
  );
  const onRequestNewChat = useCallback(() => {
    setChatId(null);
  }, [setChatId]);

  let chatElement: JSX.Element;
  if (chatType === ChatType.DATA_CAPTURE) {
    chatElement = <DataCaptureAssistantChat />;
  } else {
    chatElement = (
      <DeepResearchChat chatId={chatId} onCreateChat={onCreateChat} onRequestNewChat={onRequestNewChat} />
    );
  }

  return (
    <div className={classNames('sidepanel', css(styles.sidepanel))}>
      <div
        className={classNames(css(styles.sidepanelHeader), {
          [css(styles.sidepanelHeaderExpanded)]: props.isExpanded,
        })}
      >
        <ListingHeader
          isExpanded={props.isExpanded}
          crumbs={
            <SplitBreadcrumbs
              crumbs={[
                <div key="root" className={css(styles.breadcrumbsContainer)}>
                  <CrumbLink text="Deep Research" />
                  <BenchlingIcon icon="magic-wand" color={PURPLE_DARK} />
                  {BenchlingConfig.LLM_ENABLE_AI_DATA_CAPTURE_ASSISTANT && (
                    <AssistantSelector chatType={chatType} onUpdateChatType={setChatType} />
                  )}
                </div>,
              ]}
            />
          }
          buttons={<ToggleDropdown className="u-mini-spacing-right" expanded={props.isExpanded} />}
          showShadow={true}
        />
      </div>
      <div className={css(styles.chatWrapper)}>{chatElement}</div>
    </div>
  );
}

const styles = StyleSheet.create({
  sidepanel: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  sidepanelHeader: {
    padding: 0,
  },
  sidepanelHeaderExpanded: {
    padding: '16px 12px 0',
  },
  chatWrapper: {
    padding: '8px',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: '300px',
  },
  breadcrumbsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  assistantDropdown: {
    marginLeft: '8px',
  },
});

export default DeepResearchSidepanel;
